import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useGetCmpWhatsNewPageQuery } from "../../../graphql/types";
import { formatDesignDate, WHATS_NEW_STRINGS } from "../UserGuide/designStrings";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloErrorPolicy";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../utils/userFacingError";

// What's New view (Monday 13282310441), Claude Design CMP Dashboard (19)
// lines 182-232 (markup) and 64 (print). Opens over the page the user clicked;
// "Back to [Page Name] Page" closes it. Texts come from the backend
// (getCmpWhatsNewPage), UI strings from designStrings.ts. Print shows only this view.

// Errors stay on this view; unexpected failures still reach Sentry.
export const WHATS_NEW_GUIDE_CONTEXT = CALLER_HANDLED_USER_ERROR_CONTEXT;

const PRINT_CSS =
  "@media print{body>*:not([data-wn-root]){display:none!important}[data-wn-noprint]{display:none!important}" +
  "[data-wn-root],[data-wn-view]{position:static!important;overflow:visible!important;background:#fff!important;width:100%!important;height:auto!important}" +
  "html,body,#root{height:auto!important;overflow:visible!important}}";

const button: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, borderRadius: 8, padding: "10px 16px",
  cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
};
const card: React.CSSProperties = {
  background: "#fff", border: "1px solid #ECECEC", borderRadius: 12, padding: "22px 26px", display: "flex", flexDirection: "column", gap: 10,
  boxShadow: "0 1px 2px rgba(0,0,0,.04)", breakInside: "avoid",
};
const list: React.CSSProperties = { margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6, fontSize: 14, lineHeight: 1.55, color: "#0A0A0A" };

type Group = { page: string; items: string[] };

const Groups: React.FC<{ groups: Group[] }> = ({ groups }) => (
  <>
    {groups.map((group, index) => (
      <div key={`${group.page}-${index}`} style={card}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0A" }}>{group.page}</div>
        <ul style={list}>
          {group.items.map((item, itemIndex) => (
            <li key={itemIndex} style={{ textWrap: "pretty" } as React.CSSProperties}>{item}</li>
          ))}
        </ul>
      </div>
    ))}
  </>
);

export const CmpWhatsNewView: React.FC<{ pageKey: string; onBack: () => void }> = ({ pageKey, onBack }) => {
  const [previousMode, setPreviousMode] = useState(false);
  const { data, error } = useGetCmpWhatsNewPageQuery({ variables: { key: pageKey }, fetchPolicy: "cache-and-network", context: WHATS_NEW_GUIDE_CONTEXT });
  const page = data?.getCmpWhatsNewPage;
  const viewRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPreviousMode(false);
  }, [pageKey]);

  const scrollTop = () => {
    if (viewRef.current) viewRef.current.scrollTop = 0;
  };

  return (
    <div data-wn-root="true">
      <style>{PRINT_CSS}</style>
      <div
        ref={viewRef}
        data-testid="cmp-whats-new"
        data-wn-view="true"
        data-screen-label="Whats New"
        className="fixed bottom-0 left-0 right-0 top-0 md:left-[256px]"
        style={{ zIndex: WHATS_NEW_Z_INDEX, background: "#F7F7F8", overflowY: "auto", fontFamily: "'DM Sans',sans-serif" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 40px 240px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div data-wn-noprint="true" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button type="button" onClick={onBack} style={{ ...button, border: "none", background: "#E1A731", color: "#fff" }} className="hover:!bg-[#C6912A]">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              {page ? WHATS_NEW_STRINGS.back(page.name) : "Back"}
            </button>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {!previousMode && (
                <button type="button" onClick={() => { setPreviousMode(true); scrollTop(); }} style={{ ...button, border: "1px solid #0A0A0A", background: "#fff", color: "#0A0A0A" }} className="hover:!bg-[#F7F7F8]">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></svg>
                  {WHATS_NEW_STRINGS.previousUpdates}
                </button>
              )}
              {previousMode && (
                <button type="button" onClick={() => { setPreviousMode(false); scrollTop(); }} style={{ ...button, border: "none", background: "#0A0A0A", color: "#FFFFFF" }} className="hover:!bg-[#2A2A2A]">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                  {WHATS_NEW_STRINGS.backToLatest}
                </button>
              )}
              <button type="button" onClick={() => window.print()} style={{ ...button, border: "none", background: "#E1A731", color: "#fff" }} className="hover:!bg-[#C6912A]">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect width="12" height="8" x="6" y="14" /></svg>
                {WHATS_NEW_STRINGS.print}
              </button>
            </div>
          </div>
          {error && !page && (
            <div role="alert" style={{ fontSize: 14, color: "#B91C1C" }}>{getUserFacingErrorText(error, buildStaffErrorFallback("load this What's New page"))}</div>
          )}
          {page && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 28, fontWeight: 600, color: "#0A0A0A" }}>{page.name}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 12, whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".06em", color: "#FFFFFF", background: "#F21B1B", borderRadius: 6, padding: "5px 10px" }}>{WHATS_NEW_STRINGS.pill}</span>
                    {!previousMode && <span style={{ fontSize: 14, color: "#0A0A0A" }}>{WHATS_NEW_STRINGS.asOf(formatDesignDate(page.date))}</span>}
                    {previousMode && <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0A" }}>{WHATS_NEW_STRINGS.previousHeading}</span>}
                  </span>
                </div>
                <div style={{ fontSize: 14, color: "#0A0A0A" }}>{previousMode ? WHATS_NEW_STRINGS.previousLead : WHATS_NEW_STRINGS.currentLead}</div>
              </div>
              {previousMode ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                  {!page.previous.length && <div style={{ ...card, display: "block", fontSize: 14, color: "#0A0A0A" }}>{WHATS_NEW_STRINGS.noPrevious}</div>}
                  {page.previous.map((release) => (
                    <div key={release.date} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0A" }}>{WHATS_NEW_STRINGS.asOf(formatDesignDate(release.date))}</div>
                      <Groups groups={release.groups} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <Groups groups={page.groups} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Design (21) CLAUDE.md "Pop-ups and action menus sit above IDA" (DCL:474):
 * the full-page What's New overlay sits below IDA (12 < 15) so IDA stays
 * reachable, and the page underneath is hidden while it is open so its sticky
 * headers and dropdowns cannot show through. IDA lives in the page layer
 * ([data-dashboard-scroll]), so the overlay is rendered there too (IF11 audit M2).
 */
export const WHATS_NEW_Z_INDEX = 12;

const CmpWhatsNewInPageLayer: React.FC<{ pageKey: string; onBack: () => void; target: HTMLElement }> = ({ target, ...props }) => {
  useEffect(() => {
    const content = target.querySelector<HTMLElement>("[data-dashboard-content]");
    if (!content) return undefined;
    const previous = content.style.visibility;
    content.style.visibility = "hidden";
    return () => { content.style.visibility = previous; };
  }, [target]);
  return createPortal(<CmpWhatsNewView {...props} />, target);
};

/** Rendered by the sidebar over the page the user clicked. */
const CmpWhatsNew: React.FC<{ pageKey: string; onBack: () => void }> = (props) => {
  if (typeof document === "undefined") return null;
  const pageLayer = document.querySelector<HTMLElement>("[data-dashboard-scroll]");
  return pageLayer
    ? <CmpWhatsNewInPageLayer {...props} target={pageLayer} />
    : createPortal(<CmpWhatsNewView {...props} />, document.body);
};

export default CmpWhatsNew;
